import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminAreaController } from '@/api/generated/endpoints';
import { unwrap } from '@/api/http';
import type { AreaDTO } from '@/api/generated/types';

export interface MarketOption {
  value: number;
  label: string;
}

/**
 * All markets the caller may see (the gateway scopes the search to the caller). Like the legacy
 * console, one search without `pagination`: area-service then returns every matching market.
 */
export function useMarkets() {
  return useQuery({
    queryKey: ['areas', 'all'],
    queryFn: async () => {
      const page = unwrap(await adminAreaController.searchAreas({ areaCriteria: { withFeature: false } }));
      const content: AreaDTO[] = page?.content ?? [];
      return { content };
    },
    staleTime: 5 * 60_000,
  });
}

/** The caller's markets as select options (sorted by name) and an id -> name lookup. */
export function useMarketCatalog() {
  const { data, isLoading } = useMarkets();
  return useMemo(() => {
    const options: MarketOption[] = (data?.content ?? [])
      .filter((a) => a.id != null)
      .map((a) => ({ value: a.id as number, label: a.name ?? `#${a.id}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const names = new Map(options.map((o) => [o.value, o.label]));
    return {
      options,
      ids: options.map((o) => o.value),
      names,
      /** Market name; "—" when there is no market, "#id" when it is not in the caller's list. */
      name: (id: number | null | undefined) => (id == null ? '—' : (names.get(id) ?? `#${id}`)),
      isLoading,
    };
  }, [data, isLoading]);
}
